import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { duskColor } from './DayNight';

// The sky dome. On a clear day it shows the HDR photo sky exactly as before. For other climates it
// blends to a procedural sky with clouds that match the climate (see `cover`, `dark`, `replace` and
// `wind` in CLIMATES): fast, stretched streaks when windy; a heavy dark sheet in a storm; a pale
// blanket for snow. Opaque on purpose, so the glass roof (transmission) shows it.

const vertexShader = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vDir;
  void main() {
    vUv = uv;
    vDir = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  uniform sampler2D map;
  uniform vec3 uTint;      // night / theatre darkening and lightning flashes
  uniform float uCover;
  uniform float uDark;
  uniform float uReplace;
  uniform float uWind;
  uniform vec2 uWindDir;   // where the wind blows towards (ground plane x, z)
  uniform float uDrift;    // how far the clouds have travelled along the wind
  uniform float uDusk;     // sunrise / sunset glow (0..1)
  uniform vec3 uDuskColor;
  uniform vec3 uDuskMid;    // the pale band above the glow
  uniform vec3 uDuskZenith; // the sky high above at sunrise / sunset
  uniform float uFog;
  uniform vec3 uFogColor;
  varying vec2 vUv;
  varying vec3 vDir;

  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float noise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
  }
  float fbm(vec2 p) {
    float v = 0.0, a = 0.5;
    mat2 m = mat2(1.6, 1.2, -1.2, 1.6);
    for (int i = 0; i < 5; i++) { v += a * noise(p); p = m * p; a *= 0.5; }
    return v;
  }

  void main() {
    vec3 photo = texture2D(map, vUv).rgb;
    vec3 d = normalize(vDir);
    float up = clamp(d.y, 0.0, 1.0);

    // Procedural base sky: deep blue with few clouds, flat grey when overcast, darker for storms.
    vec3 zenith = mix(vec3(0.16, 0.36, 0.80), vec3(0.46, 0.50, 0.56), uCover * uCover);
    vec3 horizon = mix(vec3(0.70, 0.82, 0.97), vec3(0.66, 0.69, 0.73), uCover);
    vec3 base = mix(horizon, zenith, pow(up, 0.6));
    base *= 1.0 - uDark * 0.85;
    vec3 sky = mix(photo, base, uReplace);

    if (d.y > 0.0 && uReplace > 0.001) {
      // Project onto a flat cloud layer above the hall, so clouds shrink towards the horizon.
      vec2 w = d.xz / (d.y + 0.12) * 2.4;
      // Work along / across the wind, so clouds travel WITH the wind (same way as rain, snow,
      // leaves and trees) and wind stretches them into streaks along it.
      vec2 p = vec2(dot(w, uWindDir), dot(w, vec2(-uWindDir.y, uWindDir.x)));
      p.x /= 1.0 + uWind * 2.5;
      p.x -= uDrift;
      float n = fbm(p);
      float detail = fbm(p * 2.4 - vec2(uDrift * 0.7, 0.0) + 3.1); // detail drifts a little faster: clouds "boil"
      float density = n * 0.72 + detail * 0.28;
      float thresh = 0.86 - uCover * 0.62;
      float c = smoothstep(thresh, thresh + 0.22, density);
      // Fake sunlight: compare with a sample nudged towards the sun → darker undersides, bright rims.
      float towardsSun = fbm(p + vec2(0.07, 0.05));
      float light = clamp(1.05 - (towardsSun - n) * 3.5, 0.45, 1.25);
      vec3 cloudCol = mix(vec3(1.0, 0.99, 0.97), vec3(0.08, 0.09, 0.11), uDark) * light;
      cloudCol *= mix(1.0, 0.75, smoothstep(0.55, 1.0, c) * uDark); // thick storm cores are darker
      float horizonFade = smoothstep(0.0, 0.16, d.y);
      sky = mix(sky, cloudCol, c * horizonFade * clamp(uReplace * 1.2, 0.0, 1.0));
    }

    // Golden hour: blend towards a sunrise/sunset gradient (warm at the horizon, blue higher up),
    // keeping the clouds' shapes, and add a bright glow along the horizon.
    vec3 duskSky = up < 0.3 ? mix(uDuskColor, uDuskMid, up / 0.3) : mix(uDuskMid, uDuskZenith, (up - 0.3) / 0.7);
    float lum = dot(sky, vec3(0.3, 0.59, 0.11));
    sky = mix(sky, duskSky * (0.6 + lum * 0.8), uDusk * 0.62);
    sky += uDuskColor * pow(1.0 - up, 4.0) * (1.0 - uCover * 0.6) * uDusk * 0.35;

    sky = mix(sky, uFogColor, uFog);
    sky *= uTint;
    gl_FragColor = vec4(sky, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

const NIGHT_TINT = new THREE.Color('#0a1330');
const MID_EVENING = new THREE.Color('#f2a27c');    // salmon band above a sunset
const MID_MORNING = new THREE.Color('#fbf0de');    // warm white band above a sunrise
const ZENITH_EVENING = new THREE.Color('#3f4c8c'); // deep blue above a sunset
const ZENITH_MORNING = new THREE.Color('#8fbbe6'); // fresh pale blue above a sunrise
const DAY_FOG = new THREE.Color('#c9ced6');
const NIGHT_FOG = new THREE.Color('#0c111d');

export const CloudSky = ({ texture, nightRef, skyRef, weatherRef, theatreRef }) => {
  const meshRef = useRef();
  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms: {
          map: { value: texture },
          uTint: { value: new THREE.Color(1, 1, 1) },
          uCover: { value: 0 },
          uDark: { value: 0 },
          uReplace: { value: 0 },
          uWind: { value: 0 },
          uWindDir: { value: new THREE.Vector2(1, 0) },
          uDrift: { value: 0 },
          uDusk: { value: 0 },
          uDuskColor: { value: new THREE.Color('#ff7b3e') },
          uDuskMid: { value: new THREE.Color('#f6b48a') },
          uDuskZenith: { value: new THREE.Color('#3d4f8c') },
          uFog: { value: 0 },
          uFogColor: { value: new THREE.Color() },
        },
        vertexShader,
        fragmentShader,
        side: THREE.BackSide,
      }),
    [texture]
  );

  useFrame((_, delta) => {
    const u = material.uniforms;
    const w = weatherRef ? weatherRef.current : { cover: 0, dark: 0, replace: 0, wind: 0, fog: 0, rain: 0, snow: 0, flash: 0 };
    const n = nightRef ? nightRef.current : 0;
    const t = theatreRef ? theatreRef.current : 0;
    u.uCover.value = w.cover || 0;
    u.uDark.value = w.dark || 0;
    u.uReplace.value = w.replace || 0;
    u.uWind.value = w.wind || 0;
    // Drift: a lazy crawl on calm days, racing in wind and storms. Accumulated so speed changes are smooth.
    const speed = 0.02 + (w.wind || 0) * 0.25;
    u.uDrift.value += speed * Math.min(delta, 0.1);
    if (w.windX !== undefined) u.uWindDir.value.set(w.windX, w.windZ);
    if (skyRef) {
      u.uDusk.value = skyRef.current.dusk;
      duskColor(skyRef.current, u.uDuskColor.value);
      u.uDuskMid.value.copy(MID_EVENING).lerp(MID_MORNING, skyRef.current.dawn);
      u.uDuskZenith.value.copy(ZENITH_EVENING).lerp(ZENITH_MORNING, skyRef.current.dawn);
    }
    u.uFog.value = Math.min(1, (w.fog || 0) + (w.rain || 0) * 0.3 + (w.snow || 0) * 0.3) * 0.8;
    u.uFogColor.value.copy(DAY_FOG).lerp(NIGHT_FOG, n).multiplyScalar(1 - (w.dark || 0) * 0.6); // storm mist is dark
    u.uTint.value.setRGB(1, 1, 1).lerp(NIGHT_TINT, n).multiplyScalar(1 - 0.85 * t).addScalar((w.flash || 0) * 0.6);
    if (meshRef.current) meshRef.current.rotation.y += 0.00007; // the photo sky slowly turns, as before
  });

  return (
    <mesh ref={meshRef} renderOrder={-1} material={material}>
      <sphereGeometry args={[500, 64, 32]} />
    </mesh>
  );
};
